import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { Cohort, CreateCohortWithDatasetData } from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { cohortApiSlice } from '../cohortAPISlice';
import { COHORTS_BASE_PATH } from '../constants';
import { authFetch } from './fetchers';
import { invalidateCohort } from './invalidateCohort';
import { COHORT_WRITE_MUTATION_KEY } from './mutationKeys';

export interface CreateCohortProfileInput {
  projectId: string;
  data: CreateCohortWithDatasetData;
}

/**
 * Canonical TanStack mutation for creating a cohort profile
 * (POST /cohort-profile). The legacy RTK `useCreateCohortMutation` remains
 * available for non-migrated callers; gen-3 create flows use this hook.
 *
 * Tagged with `COHORT_WRITE_MUTATION_KEY` so `useProjectPageCohortSync`
 * (mounted on the project page) invalidates the project artifact manifest +
 * activity feed on success. That is what makes a freshly created cohort show
 * up in the gen-3 project list immediately instead of after a manual refresh
 * (OR-6111) — the RTK create the gen-3 flow used previously was invisible to
 * the TanStack mutation-cache subscription.
 */
export const useCreateCohortProfileMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation<ObjectResponse<Cohort>, Error, CreateCohortProfileInput>({
    mutationKey: [COHORT_WRITE_MUTATION_KEY],
    mutationFn: ({ projectId, data }) =>
      authFetch<ObjectResponse<Cohort>>(`/${COHORTS_PATH}/${projectId}/${COHORTS_BASE_PATH}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (response, { projectId }) => {
      const cohortId = response.data?.cohortProfileId;
      if (cohortId) {
        void invalidateCohort(queryClient, { cohortId, projectId });
      }
      // Keep the legacy RTK caches in sync too: the cohort-list + projects-list
      // pages (and the still-RTK delete/rename mutations) read through these
      // tags. Mirrors the RTK `createCohort` invalidatesTags.
      dispatch(cohortApiSlice.util.invalidateTags(['cohorts', 'projects']));
    },
  });
};
